import React, { useState, useEffect } from 'react'
import { Form, Button, Alert, Spinner } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, User, Lock } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import logo from '../../assets/logo.png'
import './Login.css'

const Login = () => {
  const navigate = useNavigate()
  const { login, isLoading, error, isAuthenticated, clearError } = useAuth()

  const [formData, setFormData] = useState({
    userEmail: '',
    password: '',
    rememberMe: false
  })
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (error) {
      clearError()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await login(formData.userEmail, formData.password)
      toast.success('Login successful! Welcome back.', { autoClose: 3000 })
      navigate('/dashboard')
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-bg-shape" aria-hidden="true" />
      <div className="auth-bg-watermark" aria-hidden="true" />

      <div className="auth-card">
        <div className="auth-brand">
          <img src={logo} alt="Celeris Ventures" className="auth-logo" />
          <span className="auth-brand-name">Celeris Ventures</span>
        </div>

        <div className="form-header">
          <h1 className="form-title">Welcome to Celeris Ventures</h1>
          <p className="form-subtitle">AI-Powered Enterprise Service Management</p>
        </div>

        {error && (
          <Alert variant="danger" className="error-alert">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} className="login-form">
          <Form.Group className="form-group">
            <Form.Label className="form-label">Email Address</Form.Label>
            <div className="input-with-icon">
              <User size={18} className="input-icon" aria-hidden="true" />
              <Form.Control
                type="email"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleChange}
                placeholder="you@example.com"
                className="form-control"
                required
                disabled={isLoading}
                autoComplete="username"
              />
            </div>
          </Form.Group>

          <Form.Group className="form-group">
            <Form.Label className="form-label">Password</Form.Label>
            <div className="input-with-icon password-input-group">
              <Lock size={18} className="input-icon" aria-hidden="true" />
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="form-control"
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Form.Group>

          <div className="form-options">
            <Form.Check
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              label="Remember me"
              className="form-check"
              disabled={isLoading}
            />
            <Link to="/forgot-password" className="forgot-password">
              Forgot password?
            </Link>
          </div>

          <Button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </Form>
      </div>

      <footer className="auth-footer">
        © {new Date().getFullYear()} Celeris Venture Systems Pvt. Ltd. All rights reserved.
      </footer>
    </div>
  )
}

export default Login
